import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, Link } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './contexts/AuthContext';
import { PortalRoute, AdminRoute } from './components/ProtectedRoute';

// Layouts
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { FloatingCallButton } from './components/FloatingCallButton';
import ScrollToHash from './components/ScrollToHash';
import PortalLayout from './components/PortalLayout';
import AdminLayout from './components/AdminLayout';

// Website pages
import HomePage from './pages/HomePage';
import DienstenPage from './pages/DienstenPage';
import PortfolioPage from './pages/PortfolioPage';
import PrijzenPage from './pages/PrijzenPage';
import OverOnsPage from './pages/OverOnsPage';
import WerkenBijPage from './pages/WerkenBijPage';
import OffertePage from './pages/OffertePage';
import BlogPage from './pages/BlogPage';
import BlogDetailPage from './pages/BlogDetailPage';

// Auth pages
import LoginPage from './pages/LoginPage';
import SetupPasswordPage from './pages/SetupPasswordPage';
import RegisterPage from './pages/RegisterPage';
import OnboardingWizard from './pages/OnboardingWizard';
import VerifyEmailPage from './pages/VerifyEmailPage';

// Radar / Scan pages
import ScanPage from './pages/ScanPage';
import ScanDashboard from './pages/ScanDashboard';
import RadarPage from './pages/RadarPage';
import PrijsPage from './pages/PrijsPage';

// New platform pages
import LeadsPage from './pages/LeadsPage';
import PakkettenPage from './pages/PakkettenPage';

// Portal pages
import PortalDashboard from './pages/portal/PortalDashboard';
import OnboardingPage from './pages/portal/OnboardingPage';
import MeetingPage from './pages/portal/MeetingPage';
import ReportsPage from './pages/portal/ReportsPage';
import FilesPage from './pages/portal/FilesPage';
import MessagesPage from './pages/portal/MessagesPage';
import BillingPage from './pages/portal/BillingPage';

// Platform pages (klantdashboard)
import PlatformLayout from './components/PlatformLayout';
import PlatformDashboard from './pages/platform/PlatformDashboard';
import BrandMonitoring from './pages/platform/BrandMonitoring';
import DienstenModule from './pages/platform/DienstenModule';
import OfflineMarketing from './pages/platform/OfflineMarketing';
import WebsiteModulePage from './pages/platform/WebsiteModulePage';
import BacklinksPage from './pages/platform/BacklinksPage';
import SEOModulePage from './pages/platform/SEOModulePage';
import CheckoutModule from './pages/platform/CheckoutModule';
import IntegratiePage from './pages/platform/IntegratiePage';
import OpdrachtenPage from './pages/platform/OpdrachtenPage';
import ConcurrentenPage from './pages/platform/ConcurrentenPage';
import VoortgangDelenPage from './pages/platform/VoortgangDelenPage';
import ManagedDashboard from './pages/platform/ManagedDashboard';
import SEOPage from './pages/platform/SEOPage';
import WebsitePage from './pages/platform/WebsitePage';
import LokaalPage from './pages/platform/LokaalPage';
import ReputatiePage from './pages/platform/ReputatiePage';
import AdvertentiesPage from './pages/platform/AdvertentiesPage';
import AuthCallback from './pages/AuthCallback';
import AccountPage from './pages/platform/AccountPage';
import BedrijfsInfoPage from './pages/platform/BedrijfsInfoPage';
import AiActiesPage from './pages/platform/AiActiesPage';
import WebsiteBouwPage from './pages/platform/WebsiteBouwPage';
import VersieHistoriePage from './pages/platform/VersieHistoriePage';
import MijnFacturenPage from './pages/platform/MijnFacturenPage';
import WebsiteAanvraagPage from './pages/platform/WebsiteAanvraagPage';
import WebsiteLatenBouwenPage from './pages/WebsiteLatenBouwenPage';
import WebsiteChatPage from './pages/platform/WebsiteChatPage';
import BillingInfoPage from './pages/platform/BillingInfoPage';
import AffiliateDashboardPage from './pages/affiliate/AffiliateDashboardPage';
import AffiliateSignupPage from './pages/AffiliateSignupPage';
import PartnerWordenPage from './pages/PartnerWordenPage';
import IntakePlannenPage from './pages/platform/IntakePlannenPage';
import MijnCalendlyPage from './pages/platform/MijnCalendlyPage';
import WachtwoordWijzigenPage from './pages/platform/WachtwoordWijzigenPage';
import TweeFactorPage from './pages/platform/TweeFactorPage';
import useVisitTracker from './hooks/useVisitTracker';
import usePromoCapture from './hooks/usePromoCapture';
import useGoogleAnalytics from './hooks/useGoogleAnalytics';
import { CookieConsent } from './components/CookieConsent';
import TeamDashboardPage from './pages/team/TeamDashboardPage';

// Admin pages — geconsolideerd in /components/AdminRoutes.jsx
// (apart bestand om babel stack-overflow op App.js te vermijden)
import { adminRoutes } from './components/AdminRoutes';
import TrafficPage from './pages/admin/TrafficPage';
import WerkwijzePage from './pages/WerkwijzePage';
import KlantdashboardPage from './pages/KlantdashboardPage';
import ContactPage from './pages/ContactPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import { SERVICE_DETAILS_LIST } from './data/serviceDetails';

// Dienst pagina's
import DienstTemplate from './components/DienstTemplate';
import { getDienstByRoute } from './data/diensten';

const WebLayout = ({ children }) => (
  <div className="App">
    <Header />
    <main>{children}</main>
    <Footer />
  </div>
);

// Mount the visit tracker — must be inside BrowserRouter to access useLocation
const TrackerMount = () => {
  useVisitTracker();
  usePromoCapture();
  useGoogleAnalytics();
  return null;
};

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <BrowserRouter>
          <TrackerMount />
          <ScrollToHash />
          <CookieConsent />
          <Routes>
            {/* Website */}
            <Route path="/" element={<WebLayout><HomePage /></WebLayout>} />
            <Route path="/diensten" element={<WebLayout><DienstenPage /></WebLayout>} />
            <Route path="/portfolio" element={<WebLayout><PortfolioPage /></WebLayout>} />
            <Route path="/prijzen" element={<WebLayout><PrijzenPage /></WebLayout>} />
            <Route path="/over-ons" element={<WebLayout><OverOnsPage /></WebLayout>} />
            <Route path="/werken-bij" element={<WebLayout><WerkenBijPage /></WebLayout>} />
            <Route path="/vacatures" element={<WebLayout><WerkenBijPage /></WebLayout>} />
            <Route path="/offerte" element={<WebLayout><OffertePage /></WebLayout>} />
            <Route path="/blog" element={<WebLayout><BlogPage /></WebLayout>} />
            <Route path="/blog/:slug" element={<WebLayout><BlogDetailPage /></WebLayout>} />

            {/* Publieke landing voor bezoekers zonder website */}
            <Route path="/website-laten-bouwen" element={<WebLayout><WebsiteLatenBouwenPage /></WebLayout>} />

            {/* Dienst pagina's */}
            {['/seo','/google-ads','/lead-generatie','/website-laten-maken','/social-media-advertising','/marketing-strategie','/funnels','/advertising-setup','/content-marketing','/strategie-en-groei'].map(route => (
              <Route key={route} path={route} element={
                <WebLayout><DienstTemplate dienst={getDienstByRoute(route)} /></WebLayout>
              } />
            ))}

            {/* Nieuwe website pagina's */}
            <Route path="/werkwijze" element={<WebLayout><WerkwijzePage /></WebLayout>} />
            <Route path="/klantdashboard" element={<WebLayout><KlantdashboardPage /></WebLayout>} />
            <Route path="/contact" element={<WebLayout><ContactPage /></WebLayout>} />

            {/* SEO service-subpages — FASE 3 (12 stuks via serviceDetails.js) */}
            {SERVICE_DETAILS_LIST.map((s) => (
              <Route
                key={s.slug}
                path={`/${s.slug}`}
                element={<WebLayout><ServiceDetailPage slug={s.slug} /></WebLayout>}
              />
            ))}

            {/* Auth */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/auth/callback" element={<AuthCallback />} />
            <Route path="/portal/setup" element={<SetupPasswordPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/verify-email" element={<VerifyEmailPage />} />
            <Route path="/onboarding" element={<OnboardingWizard />} />

            {/* Radar / Scan */}
            <Route path="/scan" element={<ScanPage />} />
            <Route path="/dashboard" element={<ScanDashboard />} />
            <Route path="/radar" element={<WebLayout><RadarPage /></WebLayout>} />
            <Route path="/prijs" element={<WebLayout><PrijsPage /></WebLayout>} />
            <Route path="/leads" element={<WebLayout><LeadsPage /></WebLayout>} />
            <Route path="/pakketten" element={<WebLayout><PakkettenPage /></WebLayout>} />

            {/* Platform routes */}
            <Route path="/platform" element={<PortalRoute><PlatformLayout><PlatformDashboard /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/opdrachten" element={<PortalRoute><PlatformLayout><OpdrachtenPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/brand" element={<PortalRoute><PlatformLayout><BrandMonitoring /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/diensten" element={<PortalRoute><PlatformLayout><DienstenModule /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/beheer" element={<PortalRoute><PlatformLayout><ManagedDashboard /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/offline" element={<PortalRoute><PlatformLayout><OfflineMarketing /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/checkout" element={<PortalRoute><PlatformLayout><CheckoutModule /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/checkout/success" element={<PortalRoute><PlatformLayout><CheckoutModule /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/profiel" element={<PortalRoute><PlatformLayout><WebsiteModulePage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/seo/opdrachten" element={<PortalRoute><PlatformLayout><OpdrachtenPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/integraties" element={<PortalRoute><PlatformLayout><IntegratiePage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/backlinks" element={<PortalRoute><PlatformLayout><BacklinksPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/concurrenten" element={<PortalRoute><PlatformLayout><ConcurrentenPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/delen" element={<PortalRoute><PlatformLayout><VoortgangDelenPage /></PlatformLayout></PortalRoute>} />

            {/* Module pages — scan data per categorie */}
            <Route path="/platform/seo" element={<PortalRoute><PlatformLayout><SEOPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/seo/zoekwoorden" element={<PortalRoute><PlatformLayout><SEOPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/seo/lokaal" element={<PortalRoute><PlatformLayout><LokaalPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/seo/analytics" element={<PortalRoute><PlatformLayout><WebsitePage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/website" element={<PortalRoute><PlatformLayout><WebsitePage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/aanwezigheid" element={<PortalRoute><PlatformLayout><LokaalPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/reputatie" element={<PortalRoute><PlatformLayout><ReputatiePage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/reviews" element={<PortalRoute><PlatformLayout><ReputatiePage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/google-ads" element={<PortalRoute><PlatformLayout><AdvertentiesPage /></PlatformLayout></PortalRoute>} />

            {/* Overige platform pagina's */}
            <Route path="/platform/instellingen" element={<PortalRoute><PlatformLayout><BedrijfsInfoPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/instellingen/bedrijf" element={<PortalRoute><PlatformLayout><BedrijfsInfoPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/instellingen/account" element={<PortalRoute><PlatformLayout><AccountPage /></PlatformLayout></PortalRoute>} />

            {/* AI Engine — klant zicht */}
            <Route path="/platform/ai-acties" element={<PortalRoute><PlatformLayout><AiActiesPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/website-bouw" element={<PortalRoute><PlatformLayout><WebsiteBouwPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/versies" element={<PortalRoute><PlatformLayout><VersieHistoriePage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/facturen" element={<PortalRoute><PlatformLayout><MijnFacturenPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/website-aanvraag" element={<PortalRoute><PlatformLayout><WebsiteAanvraagPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/website-chat" element={<PortalRoute><PlatformLayout><WebsiteChatPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/billing-info" element={<PortalRoute><PlatformLayout><BillingInfoPage /></PlatformLayout></PortalRoute>} />
            <Route path="/affiliate" element={<PortalRoute><PlatformLayout><AffiliateDashboardPage /></PlatformLayout></PortalRoute>} />
            <Route path="/affiliate/aanmelden" element={<AffiliateSignupPage />} />
            <Route path="/partner-worden" element={<WebLayout><PartnerWordenPage /></WebLayout>} />
            <Route path="/platform/intake-plannen" element={<PortalRoute><PlatformLayout><IntakePlannenPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/mijn-calendly" element={<PortalRoute><PlatformLayout><MijnCalendlyPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/wachtwoord-wijzigen" element={<PortalRoute><PlatformLayout><WachtwoordWijzigenPage /></PlatformLayout></PortalRoute>} />
            <Route path="/platform/2fa" element={<PortalRoute><PlatformLayout><TweeFactorPage /></PlatformLayout></PortalRoute>} />

            {/* Redirect placeholder routes to actual pages */}
            <Route path="/platform/social" element={<Navigate to="/platform/opdrachten?category=social" replace />} />
            <Route path="/platform/leads" element={<Navigate to="/platform/opdrachten" replace />} />
            <Route path="/platform/campagnes" element={<Navigate to="/platform/google-ads" replace />} />
            <Route path="/platform/vermeldingen" element={<Navigate to="/platform/aanwezigheid" replace />} />
            <Route path="/platform/inzichten" element={<Navigate to="/platform" replace />} />
            <Route path="/platform/facturatie" element={<Navigate to="/platform/instellingen/account" replace />} />
            <Route path="/platform/pakketten" element={<Navigate to="/platform/checkout" replace />} />
            <Route path="/platform/koppelingen" element={<Navigate to="/platform/integraties" replace />} />
            <Route path="/portal/onboarding" element={<PortalRoute><PortalLayout><OnboardingPage /></PortalLayout></PortalRoute>} />
            <Route path="/portal/meeting" element={<PortalRoute><PortalLayout><MeetingPage /></PortalLayout></PortalRoute>} />
            <Route path="/portal/reports" element={<PortalRoute><PortalLayout><ReportsPage /></PortalLayout></PortalRoute>} />
            <Route path="/portal/files" element={<PortalRoute><PortalLayout><FilesPage /></PortalLayout></PortalRoute>} />
            <Route path="/portal/messages" element={<PortalRoute><PortalLayout><MessagesPage /></PortalLayout></PortalRoute>} />
            <Route path="/portal/billing" element={<PortalRoute><PortalLayout><BillingPage /></PortalLayout></PortalRoute>} />

            {/* Team dashboard — FASE 2 (projectmanager/partner/teamleider/agent) */}
            <Route path="/team" element={<PortalRoute><PlatformLayout><TeamDashboardPage /></PlatformLayout></PortalRoute>} />
            <Route path="/team/dashboard" element={<PortalRoute><PlatformLayout><TeamDashboardPage /></PlatformLayout></PortalRoute>} />

            {/* Admin — geconsolideerd in components/AdminRoutes.jsx */}
            {adminRoutes}
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;
